import { t } from "i18next";
import queryString from "query-string";
import * as Sentry from "@sentry/react";
import { RouteProps } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";

import config from "@APP/config";
import { API, AppLocalStorage } from "@APP/services";
import { ICONS } from "@APP/assets";
import { useAppSelector } from "@APP/redux";
import {
  findErrorCodeByMessage,
  GENERIC_ERROR_TITLE,
  isServerError,
  SERVER_ERROR_MESSAGE,
} from "@APP/types/errorCodes";
import { Message, MoneyhubLegalInfo, PageLayout } from "@APP/components";
import { ATTACHEMENTS, PaymentStatus, PAYMENT_PROVIDER } from "@APP/types";
import { formatCurrency } from "@APP/utils";
import { downloadInvoice } from "@APP/utils/invoice";
import { useAlert } from "@APP/hooks";
import { getModalCommonButton } from "@APP/views/DeclineRTPScreen/getModalContent";
import { getModalMessageDownloadInvoiceFailure } from "@APP/components/RTPSummary/getModalContent";
import { DESKTOP_CTA_SX } from "@APP/styles/cta";
import { MOBILE_CTA_HEIGHT, MOBILE_FONT, MOBILE_ICON } from "@APP/styles/mobile";

const DETAIL_SX = { fontSize: { xs: `${MOBILE_FONT.body}px`, sm: "16px" } };

const REFERENCE_SX = { ...DETAIL_SX, wordBreak: "break-word" as const };

const DOWNLOAD_ROW_SX = { ...DETAIL_SX, mt: { xs: "16px", sm: "24px" } };

const DOWNLOAD_LINK_SX = {
  minWidth: 0,
  p: 0,
  fontSize: "inherit",
  fontWeight: 400,
  lineHeight: "inherit",
  verticalAlign: "baseline",
  textDecoration: "underline",
  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
};

const ERROR_MESSAGES = {
  CHECK_BANK:
    "Check your bank account to see if the payment has been made. If it hasn't, you can try and pay again.",
  CANCELLED:
    "You cancelled the payment with your bank. If there is a problem with the payment request, contact the sender to discuss.",
  MAVERICK_ERROR:
    "We are unable to update the status of your payment. Please try again to update your payment status.",
  UNSUCCESSFUL:
    "Your bank did not complete your payment successfully. Please try again in a few moments.",
  NOT_AUTHORISED:
    "Your payment was not authorised by your bank. You can try and pay again or contact your bank for more information",
};

const withMobileLineBreaks = (message: string) =>
  message.replace(/\.\s+(?=[A-Z])/g, ".<br /><br />");

// Title of the payer-cancelled state.
const CANCELLED_TITLE = "Payment cancelled";

// Title of the "we never got a usable answer" state — the payment may or may
// not have gone through, so the payer is pointed at their bank statement.
const UNCONFIRMED_TITLE = "We're not sure if the payment was successful";

// Title of the definite-failure state: the bank rejected the payment.
const UNSUCCESSFUL_TITLE = "Payment unsuccessful";

// Title of the not-authorised state: the payment never got past the bank's
// authorisation step.
const NOT_AUTHORISED_TITLE = "Your payment wasn't authorised";

const NOT_AUTHORISED_ERRORS = [
  "interaction_required",
  "login_required",
  "account_selection_required",
  "consent_required",
];

const UNSUCCESSFUL_ERRORS = [
  "invalid_request",
  "unauthorized_client",
  "unsupported_response_type",
  "invalid_scope",
  "server_error",
  "temporarily_unavailable",
  "invalid_request_uri",
  "invalid_request_object",
  "request_not_supported",
  "request_uri_not_supported",
  "registration_not_supported",
];

const REJECTED_ERROR = "PAYMENT_REJECTED";
const PENDING_ERROR = "PAYMENT_PENDING";

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 120_000;

const SETTLED_STATUSES = [
  PaymentStatus.Authorized,
  PaymentStatus.Authorised,
  PaymentStatus.Accepted,
  PaymentStatus.Settled,
];

type PollOutcome = "settled" | "rejected" | "unresolved";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface ConfirmationInfo {
  sender: string;
  amount: { amount: string; currency: string };
  bank?: string;
  reference: string;
  invoiceUrl?: string;
}

const RTPCompletePaymentScreen: React.FC<RouteProps> = ({ location }) => {
  const theme = useTheme();
  const { alert } = useAlert();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const rtpState = useAppSelector((state) => state.rtp);

  const [loading, setLoading] = useState(true);
  const [rtpNotAvailable, setRtpNotAvailable] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [errorMessageTitle, setErrorMessageTitle] = useState<string>(GENERIC_ERROR_TITLE);
  const [loaderMessage, setLoaderMessage] = useState<string>("");
  const [confirmationInfo, setConfirmationInfo] = useState<ConfirmationInfo>();

  // Guards the no-response timeout below: once the screen has reached any
  // terminal state (confirmed / error / link unavailable) the timer must not
  // overwrite it.
  const outcomeSettled = useRef(false);
  const noResponseTimer = useRef<ReturnType<typeof setTimeout>>();

  // Lets the pending-payment poll stop early instead of running out its full
  // two minutes against an unmounted component.
  const mounted = useRef(true);

  const stopNoResponseTimer = () => {
    if (noResponseTimer.current) clearTimeout(noResponseTimer.current);
  };

  const settleOutcome = () => {
    outcomeSettled.current = true;
    stopNoResponseTimer();
  };

  const setConfirmed = (info: ConfirmationInfo) => {
    settleOutcome();
    setConfirmationInfo(info);
  };

  const showPaymentUnconfirmed = () => {
    settleOutcome();
    setLoaderMessage("");
    setErrorMessageTitle(UNCONFIRMED_TITLE);
    setErrorMessage(ERROR_MESSAGES.CHECK_BANK);
    setLoading(false);
  };

  // Shared shape of the two definite-failure designs: the brand red-circle
  // error mark, a headline, a white card of copy and a Close action.
  const showErrorState = (title: string, message: string) => {
    settleOutcome();
    setLoaderMessage("");
    setErrorMessageTitle(title);
    setErrorMessage(message);
    setLoading(false);
  };

  const showPaymentUnsuccessful = () => {
    showErrorState(UNSUCCESSFUL_TITLE, ERROR_MESSAGES.UNSUCCESSFUL);
  };

  const showPaymentNotAuthorised = () => {
    showErrorState(NOT_AUTHORISED_TITLE, ERROR_MESSAGES.NOT_AUTHORISED);
  };

  const showServerError = () => {
    showErrorState(GENERIC_ERROR_TITLE, SERVER_ERROR_MESSAGE);
  };

  const queryParams = queryString.parse(location?.hash ?? "");
  const {
    code,
    state,
    error,
    error_description,
    status: maverickPaymentStatus,
    id: maverickPaymentId,
  } = queryParams;

  const pollForSettlement = async (paymentStateId: string): Promise<PollOutcome> => {
    stopNoResponseTimer();
    setLoaderMessage("Payment sending");

    const deadline = Date.now() + POLL_TIMEOUT_MS;

    while (Date.now() < deadline) {
      await wait(POLL_INTERVAL_MS);
      if (!mounted.current) return "unresolved";

      try {
        const { context } = await API.getPaymentState(paymentStateId);

        if (SETTLED_STATUSES.includes(context.status)) return "settled";
        if (context.status === PaymentStatus.Rejected) return "rejected";
      } catch (e) {
        Sentry.captureMessage(
          `Pending payment poll failed, ${JSON.stringify((e as any)?.response?.data ?? e)}`,
        );
      }
    }

    Sentry.captureMessage("Pending payment did not resolve before the poll deadline");
    return "unresolved";
  };

  const processMoneyhubPayment = async () => {
    let confirmationStarted = false;

    try {
      //
      // STEP 1: get payment state with all the necessary data for confirmation.
      //
      const paymentState = await API.getPaymentState(state as string);
      const { consentId, meta } = paymentState;
      const custodianId = paymentState.context.custodianId;
      const paymentStatus = paymentState.context.status;
      if (paymentStatus === PaymentStatus.Authorized) {
        settleOutcome();
        setRtpNotAvailable(true);
      } else {
        setLoaderMessage("Payment sending");
        const { BankId, redirectUrl, invoiceUrl, initialInvoiceType, erpId, features } =
          meta.reduce((prev, curr) => {
            if (
              curr.key &&
              [
                "BankId",
                "redirectUrl",
                "invoiceUrl",
                "initialInvoiceType",
                "erpId",
                "features",
              ].includes(curr.key)
            ) {
              return { ...prev, [curr.key]: curr.value };
            }
            return prev;
          }, {} as { [key: string]: string });

        // Download button should be displayed in case of external invoice or internal native invoice:
        const shouldDisplayDownloadLink =
          invoiceUrl &&
          (erpId !== "internal" || features.includes("Native")) &&
          !rtpState.rtp?.isBulkRtp;

        if (rtpState.rtp?.standingOrder) {
          //
          // STEP 2: Confirm payment.
          //
          confirmationStarted = true;
          const paymentConfirmResponse = await API.confirmStandingOrderPayment(
            custodianId,
            consentId,
            code as string,
            redirectUrl,
            { additionalData: { initialInvoiceType } },
          );

          if (!paymentConfirmResponse) {
            throw new Error("'processPaymentConfirmation()' invalid response");
          }

          if (paymentConfirmResponse.status === PaymentStatus.Rejected) {
            throw new Error(REJECTED_ERROR);
          }

          if (paymentConfirmResponse.status === PaymentStatus.Pending) {
            throw new Error(PENDING_ERROR);
          }

          // STEP 3: Update RTP with standing order.
          const rtpUpdateResponse = await API.updateStandingOrderPayment(
            rtpState.rtpData!.paymentRequestId!,
            paymentConfirmResponse.paymentId,
            paymentConfirmResponse.payment.firstPaymentDateTime,
          );
          setConfirmed({
            amount: paymentConfirmResponse.payment.firstPaymentAmount,
            bank: custodianId,
            sender:
              rtpUpdateResponse.supplier.businessContact?.name ?? rtpUpdateResponse.supplier.name,
            reference: paymentConfirmResponse.paymentId,
            invoiceUrl: shouldDisplayDownloadLink ? invoiceUrl : "",
          });
        } else {
          //
          // STEP 2: Confirm payment.
          //
          confirmationStarted = true;
          await authorizePayment(
            BankId,
            consentId,
            code as string,
            redirectUrl,
            initialInvoiceType,
            shouldDisplayDownloadLink,
            invoiceUrl,
          );
        }
      }
    } catch (error: any) {
      Sentry.captureMessage(
        `Payment confirmation error,", ${JSON.stringify(error?.response?.data ?? error)}`,
      );
      setLoaderMessage("");

      if (error?.message?.includes(REJECTED_ERROR)) {
        showPaymentUnsuccessful();
      } else if (isServerError(error) && !confirmationStarted) {
        showServerError();
      } else {
        // Everything else — including a still-pending payment — leaves us
        // unable to say whether the money moved.
        showPaymentUnconfirmed();
      }
    }
    setLoading(false);
  };

  const checkPaymentStatus = async (
    BankId: string,
    consentId: string,
    code: string,
    redirectUrl: string,
    initialInvoiceType: any,
    shouldDisplayDownloadLink: any,
    invoiceUrl: string,
    retryCount: number = 3,
    retryInterval: number = 5000,
  ) => {
    for (let attempt = 1; attempt <= retryCount; attempt++) {
      try {
        const paymentConfirmResponse = await API.authorizePayment(
          BankId,
          consentId,
          code as string,
          redirectUrl,
          { additionalData: { initialInvoiceType } },
        );

        if (paymentConfirmResponse && paymentConfirmResponse.status === PaymentStatus.Pending) {
          // The bank has the payment but no outcome yet. Watch the consent
          // state rather than deciding on the payer's behalf.
          const outcome = await pollForSettlement(state as string);

          if (outcome === "rejected") throw new Error(REJECTED_ERROR);
          if (outcome === "unresolved") throw new Error(PENDING_ERROR);

          // Settled. The consent-state response carries no payment id or
          // amount, so the success screen is filled from the RTP the same way
          // the Maverick and card flows do.
          setConfirmed({
            amount: rtpState.rtp!.amount,
            bank: BankId,
            sender: rtpState.senderData?.businessContact?.name ?? rtpState.senderData!.name,
            reference: rtpState.rtp!.receivable.reference,
            invoiceUrl: shouldDisplayDownloadLink ? invoiceUrl : "",
          });
          return;
        } else if (
          paymentConfirmResponse &&
          paymentConfirmResponse.status === PaymentStatus.Rejected
        ) {
          // The bank rejected the payment. This is a final answer, so it is not
          // retried — the non-503 branch below rethrows it straight away.
          throw new Error(REJECTED_ERROR);
        } else if (paymentConfirmResponse) {
          // Payment was successful; set the confirmation info
          setConfirmed({
            amount: paymentConfirmResponse.payment.amount,
            bank: BankId,
            sender: rtpState.senderData?.businessContact?.name ?? rtpState.senderData!.name,
            reference: paymentConfirmResponse.paymentId,
            invoiceUrl: shouldDisplayDownloadLink ? invoiceUrl : "",
          });
          return;
        } else {
          throw new Error("'processPaymentConfirmation()' invalid response");
        }
      } catch (error: any) {
        if (attempt === retryCount) {
          Sentry.captureMessage(
            `Payment confirmation  failed after ${retryCount} attempts,", ${JSON.stringify(
              error?.response?.data ?? error,
            )}`,
          );
          throw new Error(error);
        }

        // If the error is a 503, wait for the interval before retrying
        if (error.message.includes("503")) {
          Sentry.captureMessage(
            `Payment confirmation  failed after ${attempt} attempts,", ${JSON.stringify(
              error?.response?.data ?? error,
            )}`,
          );
          await new Promise((resolve) => setTimeout(resolve, retryInterval));
        } else {
          // If it's a different error, throw it immediately
          throw error;
        }
      }
    }
  };

  const authorizePayment = async (
    BankId: string,
    consentId: string,
    code: string,
    redirectUrl: string,
    initialInvoiceType: any,
    shouldDisplayDownloadLink: any,
    invoiceUrl: string,
  ) => {
    try {
      await checkPaymentStatus(
        BankId,
        consentId,
        code,
        redirectUrl,
        initialInvoiceType,
        shouldDisplayDownloadLink,
        invoiceUrl,
      );
    } catch (error) {
      throw error;
    }
  };

  const processMaverickPayment = async () => {
    try {
      setErrorMessage(undefined);

      if (!maverickPaymentId || !maverickPaymentStatus) {
        // The provider redirected back without a payment id or status, so we
        // have no idea what happened at the card form: the payer may have
        // walked away before paying, or the redirect may be malformed after a
        // successful charge. Since we cannot tell those apart, the payer is
        // pointed at their own account rather than being told an outcome —
        // the same honest state the Moneyhub journeys use. (This previously
        // asserted the payer had changed their mind, which would be a false
        // statement about their money in the malformed-redirect case.)
        showPaymentUnconfirmed();

        return;
      }

      await API.updateMaverickPaymentStatus({
        rtpId: rtpState.rtp?.id!,
        customerId: rtpState.rtpData?.payable.supplierContact.email!,
        paymentDetails: {
          paymentId: Number(maverickPaymentId),
          status: maverickPaymentStatus as string,
        },
        amount: rtpState.rtpData?.paymentDetails.amount!,
        entityDetails: rtpState.rtpData?.payable.entityDetails!,
      });

      if (maverickPaymentStatus !== "Success") {
        // The provider did answer, and the answer was not a success — a known
        // outcome, so not the unconfirmed state.
        return showPaymentUnsuccessful();
      }

      setConfirmed({
        amount: rtpState.rtp!.amount,
        sender: rtpState.rtp!.supplier.businessContact?.name ?? rtpState.rtp!.supplier.name,
        reference: rtpState.rtp!.receivable.reference,
      });
    } catch (error) {
      // Same shared error state — the brand mark, with the "Try again" action
      // the `MAVERICK_ERROR` copy still selects in `getErrorButton`.
      showErrorState(GENERIC_ERROR_TITLE, ERROR_MESSAGES.MAVERICK_ERROR);
    }

    setLoading(false);
  };

  const processCardPayment = async () => {
    const invoiceLink = rtpState.rtpData?.payable?.attachments.find(
      (item) => item.name === ATTACHEMENTS.RECEIVABLE_PDF && item.contentType === "application/pdf",
    )?.uri;

    try {
      setConfirmed({
        amount: rtpState.rtp!.amount,
        sender: rtpState.rtp!.supplier.businessContact?.name ?? rtpState.rtp!.supplier.name,
        reference: rtpState.rtp!.receivable.reference,
        invoiceUrl: invoiceLink,
      });
      setLoading(false);
    } catch (e) {
      showPaymentUnconfirmed();
    }
  };

  const onTryAgainMaverickUpdate = () => {
    setLoading(true);
    processMaverickPayment();
  };

  const handleClose = () => {
    window.close();
  };

  const handleDownloadInvoice = async () => {
    const invoiceUrl = confirmationInfo?.invoiceUrl;
    if (!invoiceUrl) return;

    const downloaded = await downloadInvoice(invoiceUrl);
    if (!downloaded) {
      alert("", getModalMessageDownloadInvoiceFailure(), getModalCommonButton());
    }
  };

  useEffect(() => {
    noResponseTimer.current = setTimeout(() => {
      if (!outcomeSettled.current) {
        Sentry.captureMessage("Payment confirmation timed out with no response");
        showPaymentUnconfirmed();
      }
    }, POLL_TIMEOUT_MS);

    (async () => {
      const { cardPaymentFlow } = (location?.state ?? {}) as {
        cardPaymentFlow: boolean | undefined;
      };

      if ((error || !code) && !cardPaymentFlow) {
        Sentry.captureMessage(`Payment confirmation error (query param received): "${error}" `);
        const result = findErrorCodeByMessage(error as string);
        settleOutcome();
        if (error === "access_denied") {
          setErrorMessageTitle(CANCELLED_TITLE);
          setErrorMessage(ERROR_MESSAGES.CANCELLED);
        } else if (NOT_AUTHORISED_ERRORS.includes(error as string)) {
          showPaymentNotAuthorised();
        } else if (UNSUCCESSFUL_ERRORS.includes(error as string)) {
          // The bank took the request and failed it.
          showPaymentUnsuccessful();
        } else if (result.errorFound && result.errorMessage?.text) {
          setErrorMessageTitle(result.errorMessage?.title);
          setErrorMessage(result.errorMessage?.text + result.errorCode);
        } else {
          showPaymentUnconfirmed();
        }
        try {
          if (error) {
            const paymentState = await API.getPaymentState(state as string);
            const { consentId } = paymentState;
            const custodianId = paymentState?.context?.custodianId;
            await sendPisError(
              consentId,
              custodianId,
              error as string,
              error_description as string,
            );
          }
        } catch (e) {}
        setLoading(false);
        return;
      }

      if (config?.FEATURE?.SQUARE && cardPaymentFlow === true) {
        processCardPayment();
        return;
      }

      if (config.PAYMENT_PROVIDER === PAYMENT_PROVIDER.MAVERICK) {
        processMaverickPayment();
        return;
      }

      processMoneyhubPayment();
      AppLocalStorage.removeItem("orgId");
    })();

    return () => {
      mounted.current = false;
      stopNoResponseTimer();
    };
  }, []);

  // Sent PIS error  & description (if any) to BE
  const sendPisError = async (
    consentId: string,
    bankId: string,
    error: string | null,
    error_description: string,
  ) => {
    try {
      if (consentId && bankId) await API.sendPisError(bankId, consentId, error, error_description);
    } catch (e) {}
  };

  const hideHeader = isMobile && !loading && (rtpNotAvailable || !!confirmationInfo);

  // The error / unconfirmed states show no footer bar at all — same condition
  // the error branch of `renderMainContent` uses to take over the page.
  const showingError = !loading && !rtpNotAvailable && (!!errorMessage || !confirmationInfo);

  const invoiceReference =
    rtpState.rtpData?.payable?.reference ?? rtpState.rtp?.receivable?.reference;

  const renderMainContent = () => {
    if (rtpNotAvailable) {
      return (
        <Message
          style={{ margin: "auto" }}
          type="success"
          title="This link isn't available"
          iconSrc={ICONS.CONFIRM_ICON}
          iconSize={isMobile ? MOBILE_ICON.success : undefined}
          description={t("Screens.Landing.error1")}
          buttons={[
            {
              text: "Close",
              variant: "contained",
              color: "primary",
              onClick: handleClose,
              sx: isMobile ? { marginTop: "16px" } : DESKTOP_CTA_SX,
            },
          ]}
          actionsGap={0}
        />
      );
    }

    const unconfirmed = !loading && !errorMessage && !confirmationInfo;

    if (errorMessage || unconfirmed) {
      const getErrorButton = () => {
        if (errorMessage === ERROR_MESSAGES.MAVERICK_ERROR) {
          return {
            text: "Try again",
            variant: "contained" as const,
            color: "secondary" as const,
            onClick: onTryAgainMaverickUpdate,
          };
        }
        return {
          text: "Close",
          variant: "contained" as const,
          color: "secondary" as const,
          onClick: handleClose,
          sx: isMobile ? { marginTop: "16px" } : DESKTOP_CTA_SX,
        };
      };

      return (
        <Box display="flex" width="100%" alignItems="center" id="rtpCompletePaymentErrorBox">
          <Message
            type="error"
            title={unconfirmed ? UNCONFIRMED_TITLE : errorMessageTitle}
            description={
              isMobile
                ? withMobileLineBreaks(errorMessage ?? ERROR_MESSAGES.CHECK_BANK)
                : errorMessage ?? ERROR_MESSAGES.CHECK_BANK
            }
            iconSrc={ICONS.ERROR_ICON}
            iconSize={isMobile ? MOBILE_ICON.error : undefined}
            buttons={[getErrorButton()]}
            actionsGap={0}
          />
        </Box>
      );
    }

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="100%"
        pt={hideHeader ? 0 : isMobile ? "32px" : undefined}
        id="rtpCompletePaymentBox">
        {confirmationInfo && (
          <>
            <Message
              type="success"
              title="Payment successful"
              iconSrc={ICONS.CONFIRM_ICON}
              iconSize={isMobile ? MOBILE_ICON.success : undefined}
              descriptionContent={
                <>
                  <Typography sx={DETAIL_SX} id="rtpCompletePaymentSender">
                    To: {confirmationInfo.sender}
                  </Typography>
                  <Typography sx={DETAIL_SX} id="rtpCompletePaymentAmount">
                    Amount: {formatCurrency(confirmationInfo.amount)}
                  </Typography>
                  <Typography sx={REFERENCE_SX} id="rtpCompletePaymentReference">
                    Payment reference: {confirmationInfo.reference}
                  </Typography>
                  {confirmationInfo.invoiceUrl && (
                    <Typography sx={DOWNLOAD_ROW_SX} id="rtpCompletePaymentInvoice">
                      Download invoice:{" "}
                      <Button
                        sx={DOWNLOAD_LINK_SX}
                        disableRipple
                        onClick={handleDownloadInvoice}
                        id="rtpCompletePaymentDownloadInvoiceButton">
                        {invoiceReference ?? "Invoice"}
                      </Button>
                    </Typography>
                  )}
                </>
              }
            />
            {/* Desktop right-aligns the CTA; mobile stacks it full width, the
                same as the other terminal screens. */}
            <Box
              display="flex"
              width="100%"
              justifyContent={isMobile ? "stretch" : "flex-end"}
              mt={isMobile ? "8px" : "0"}
              mb={3}>
              <Button
                fullWidth={isMobile}
                sx={isMobile ? { minHeight: MOBILE_CTA_HEIGHT } : DESKTOP_CTA_SX}
                variant="contained"
                color="primary"
                onClick={handleClose}
                id="rtpCompletePaymentCloseButton">
                Close
              </Button>
            </Box>
            <MoneyhubLegalInfo />
          </>
        )}
      </Box>
    );
  };

  return (
    <PageLayout
      loading={loading}
      loaderText={loaderMessage}
      footerVariant="minimal"
      hideHeader={hideHeader}
      hideFooter={showingError}>
      <Box display="flex" alignItems="center" flexDirection="column" width="100%" mx="auto">
        {renderMainContent()}
      </Box>
    </PageLayout>
  );
};

export default RTPCompletePaymentScreen;
